import { useState, type ComponentProps, type ReactNode } from 'react';

import { Icon } from '../../icons';
import { cn, cva, type VariantProps } from '../../shared';

export const cardVariants = cva(
  [
    'bg-card text-card-foreground shadow-card gap-field flex flex-col rounded-lg border',
    // `surface` applies a backdrop-filter whose blur paints past the radius.
    'overflow-hidden',
    'surface',
    // At the base, not split across `interactive`, so the two never declare
    // `transition-property` separately and race each other.
    'transition-[box-shadow,border-color] duration-fast',
  ],
  {
    variants: {
      /**
       * Set by re-pointing the variable `surface` reads, since it declares `border-color`
       * itself. The hover swap to `--primary` isn't gated behind `interactive` — every card with
       * a real border answers hover with the brand colour; `interactive` only adds lift/focus.
       */
      border: {
        surface:
          '[--border-color-surface:hsl(var(--card-border))] hover:[--border-color-surface:hsl(var(--primary))]',
        // No hover rule: the swap below re-points this variable to `--primary`, which is where
        // this variant already holds it. Repeating it here emitted a rule that could never change
        // anything, and read as if it could.
        primary: '[--border-color-surface:hsl(var(--primary))]',
        none: 'border-0',
      },
      padding: { default: 'p-card', none: '' },
      interactive: {
        true: 'focus-ring-within hover:shadow-card-hover',
        false: '',
      },
    },
    defaultVariants: { padding: 'default', border: 'surface', interactive: false },
  },
);

type CardVariants = VariantProps<typeof cardVariants>;

export interface CardProps extends ComponentProps<'div'> {
  padding?: NonNullable<CardVariants['padding']>;
  border?: NonNullable<CardVariants['border']>;
  interactive?: boolean;
}

/** `interactive` styles the card as clickable; the click target stays a real control inside it. */
export function Card({ padding, border, interactive, className, ...props }: CardProps) {
  return (
    <div className={cn(cardVariants({ padding, border, interactive }), className)} {...props} />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('gap-field-label flex flex-col', className)} {...props} />;
}

const HEADING = { 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6' } as const;

export type CardTitleLevel = keyof typeof HEADING;

export interface CardTitleProps extends Omit<ComponentProps<'h3'>, 'children'> {
  /** The level belongs to the page outline, not the card. */
  level?: CardTitleLevel;
  children: ComponentProps<'h3'>['children'];
}

export function CardTitle({ level = 3, className, ...props }: CardTitleProps) {
  const Heading = HEADING[level];

  return <Heading className={cn('text-card-title font-semibold', className)} {...props} />;
}

export function CardDescription({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('text-caption text-muted-foreground', className)} {...props} />;
}

export function CardContent({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('gap-field flex flex-col', className)} {...props} />;
}

export function CardFooter({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('gap-inline flex items-center', className)} {...props} />;
}

const imageVariants = cva('relative w-full overflow-hidden', {
  variants: {
    aspectRatio: {
      auto: '',
      square: 'aspect-square',
      video: 'aspect-video',
      portrait: 'aspect-[3/4]',
      wide: 'aspect-[2/1]',
    },
  },
  defaultVariants: { aspectRatio: 'video' },
});

type ImageVariants = VariantProps<typeof imageVariants>;

export interface CardImageProps extends Omit<ComponentProps<'img'>, 'onError' | 'onLoad'> {
  src: string;
  alt: string;
  aspectRatio?: NonNullable<ImageVariants['aspectRatio']>;
  fallback?: ReactNode;
}

/** Reserves its box through loading and failure so the card does not reflow when the image arrives. */
export function CardImage({
  src,
  alt,
  aspectRatio,
  fallback,
  className,
  ...props
}: CardImageProps) {
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  return (
    <div className={cn(imageVariants({ aspectRatio }), 'rounded-[inherit]', className)}>
      {state === 'loading' ? <div className="bg-muted absolute inset-0 animate-pulse" /> : null}

      {state === 'failed' ? (
        <div className="bg-muted text-muted-foreground absolute inset-0 flex items-center justify-center">
          {fallback ?? <Icon name="image" size="lg" aria-hidden="true" />}
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          onLoad={() => {
            setState('ready');
          }}
          onError={() => {
            setState('failed');
          }}
          className={cn(
            'h-full w-full rounded-[inherit] object-cover transition-opacity duration-fast',
            state === 'ready' ? 'opacity-100' : 'opacity-0',
          )}
          {...props}
        />
      )}
    </div>
  );
}
